// Colour helpers, the time-of-day light model, and the sky and hills drawing.

export type RGB = [number, number, number];

export const clamp01 = (x: number): number => (x < 0 ? 0 : x > 1 ? 1 : x);
export const smooth = (a: number, b: number, x: number): number => {
  const t = clamp01((x - a) / (b - a));
  return t * t * (3 - 2 * t);
};
export const mix = (a: RGB, b: RGB, t: number): RGB => [
  a[0] + (b[0] - a[0]) * t,
  a[1] + (b[1] - a[1]) * t,
  a[2] + (b[2] - a[2]) * t,
];
/** How much colour survives in the foreground: 1 = as authored, 0 = greyscale. */
export const SATURATION = 0.5;

let muting = true;
/** Turn the foreground muting on or off. The sky and skylines are drawn with it off so they stay luminous. */
export const setMuting = (on: boolean): void => { muting = on; };

/**
 * Colours go through here. With muting on (the landscape) each colour is pulled toward its own grey,
 * warmed slightly like aged paper, and its blacks are lifted like a faded print. With muting off they pass through untouched.
 */
export const css = (c: RGB, a = 1, sat?: number): string => {
  if (!muting && sat === undefined) return `rgba(${c[0] | 0},${c[1] | 0},${c[2] | 0},${a})`;
  const k = sat ?? SATURATION;
  const l = c[0] * 0.3 + c[1] * 0.59 + c[2] * 0.11;
  const r = (l + (c[0] - l) * k) * 0.94 + 15;
  const g = (l + (c[1] - l) * k) * 0.92 + 13;
  const b = (l + (c[2] - l) * k) * 0.86 + 10;
  return `rgba(${r | 0},${g | 0},${b | 0},${a})`;
};
const hex = (s: string): RGB => [
  parseInt(s.slice(1, 3), 16),
  parseInt(s.slice(3, 5), 16),
  parseInt(s.slice(5, 7), 16),
];

/** Push a colour away from its own grey by `k` (1 = unchanged). */
const vivid = (c: RGB, k: number): RGB => {
  const l = c[0] * 0.3 + c[1] * 0.59 + c[2] * 0.11;
  return [0, 1, 2].map((i) => Math.max(0, Math.min(255, l + (c[i] - l) * k))) as RGB;
};

export const WARM: RGB = [236, 150, 92];

interface Stop { h: number; top: RGB; mid: RGB; bot: RGB }
// Saturated fields of light, like a Turrell skyspace: one colour melting into the next with no detail to look at.
const STOPS: Stop[] = [
  { h: 0, top: hex('#07031c'), mid: hex('#1c0a55'), bot: hex('#6a1f8c') },
  { h: 5, top: hex('#1a0b52'), mid: hex('#7b1fa2'), bot: hex('#ff4f7e') },
  { h: 6.5, top: hex('#3a20b0'), mid: hex('#ee4a8f'), bot: hex('#ffb347') },
  { h: 9, top: hex('#0a4fdc'), mid: hex('#26a5f2'), bot: hex('#b6f2ff') },
  { h: 13, top: hex('#0030c8'), mid: hex('#0a8cff'), bot: hex('#8aeaff') },
  { h: 17, top: hex('#1244d6'), mid: hex('#5593f2'), bot: hex('#ffe29a') },
  { h: 19, top: hex('#2c0f8e'), mid: hex('#dc3492'), bot: hex('#ff8c3a') },
  { h: 20.5, top: hex('#0d0632'), mid: hex('#3d1484'), bot: hex('#b4328c') },
  { h: 24, top: hex('#07031c'), mid: hex('#1c0a55'), bot: hex('#6a1f8c') },
];

export interface Light {
  hour: number;
  top: RGB; // sky colour at the top of the screen
  mid: RGB; // richer band between top and horizon
  bot: RGB; // sky colour at the horizon
  day: number; // 0 = deep night, 1 = full daylight
  warm: number; // 0..1 sunrise/sunset tint
}

/** Hour of the visitor's local day (0-24). `?hour=21` in the URL overrides it for testing. */
export function currentHour(): number {
  const q = new URLSearchParams(location.search).get('hour');
  if (q !== null && q !== '' && !Number.isNaN(Number(q))) return Number(q);
  const d = new Date();
  return d.getHours() + d.getMinutes() / 60 + d.getSeconds() / 3600;
}

export function lightAt(hour: number): Light {
  const hr = ((hour % 24) + 24) % 24;
  let i = 0;
  while (i < STOPS.length - 2 && hr >= STOPS[i + 1].h) i++;
  const a = STOPS[i];
  const b = STOPS[i + 1];
  const t = (hr - a.h) / (b.h - a.h);
  const day = smooth(5.5, 8, hr) * (1 - smooth(17.5, 20.5, hr));
  const warm = clamp01(Math.exp(-(((hr - 6.6) / 0.9) ** 2)) + Math.exp(-(((hr - 19) / 1.0) ** 2)));
  const k = 1.08; // a touch more saturation so the colour fields glow
  const top = vivid(mix(a.top, b.top, t), k);
  const mid = vivid(mix(a.mid, b.mid, t), k);
  const bot = vivid(mix(a.bot, b.bot, t), k);
  return { hour: hr, top, mid, bot, day, warm };
}

// ---- stars -----------------------------------------------------------------

interface Star { x: number; y: number; r: number; ph: number }
const STARS: Star[] = (() => {
  let s = 1234567;
  const rnd = () => ((s = (s * 1664525 + 1013904223) >>> 0) / 4294967296);
  return Array.from({ length: 150 }, () => ({ x: rnd(), y: rnd() * 0.55, r: 0.5 + rnd() * 1.2, ph: rnd() * 6.28 }));
})();

// ---- drawing ---------------------------------------------------------------

export const HORIZON = 0.62; // fraction of screen height where hills meet the ground

export function drawSky(ctx: CanvasRenderingContext2D, w: number, h: number, L: Light, t: number, scroll = 0, hz = HORIZON): void {
  const g = ctx.createLinearGradient(0, 0, 0, h * hz);
  g.addColorStop(0, css(L.top));
  g.addColorStop(0.55, css(L.mid));
  g.addColorStop(1, css(L.bot));
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, w, h * hz + 2);

  // glow hugging the horizon, strongest at sunrise and sunset
  const hg = ctx.createLinearGradient(0, h * hz * 0.55, 0, h * hz);
  hg.addColorStop(0, css(L.bot, 0));
  hg.addColorStop(1, css(mix(L.bot, [255, 255, 255], 0.4), 0.32 + 0.25 * L.warm));
  ctx.fillStyle = hg;
  ctx.fillRect(0, h * hz * 0.55, w, h * hz * 0.45 + 2);

  // ambient: two very soft colour blooms that drift slowly, so the sky breathes without any shapes in it
  const drift = scroll * 0.03 + t * 6;
  for (let i = 0; i < 2; i++) {
    const bx = ((((i * 0.55 + 0.15) * w - drift * (0.6 + i * 0.5)) % (w * 1.6)) + w * 1.6) % (w * 1.6) - w * 0.3;
    const by = h * hz * (0.3 + i * 0.22);
    const br = w * (0.5 + i * 0.12);
    const col = i ? mix(L.mid, WARM, L.warm * 0.6) : mix(L.top, [255, 255, 255], 0.25 * L.day);
    const bg = ctx.createRadialGradient(bx, by, 0, bx, by, br);
    bg.addColorStop(0, css(col, 0.22));
    bg.addColorStop(1, css(col, 0));
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, w, h * hz + 2);
  }

  // stars fade in with the night
  const night = Math.pow(1 - L.day, 1.6);
  if (night > 0.02) {
    ctx.fillStyle = '#fff';
    for (const s of STARS) {
      ctx.globalAlpha = night * (0.55 + 0.45 * Math.sin(t * 1.3 + s.ph));
      ctx.fillRect(s.x * w, s.y * h, s.r, s.r);
    }
    ctx.globalAlpha = 1;
  }
}

const HILL_DAY: RGB[] = [hex('#8fd684'), hex('#54b847')];
const HILL_NIGHT: RGB[] = [hex('#16301f'), hex('#0f2618')];

export function drawHills(
  ctx: CanvasRenderingContext2D, w: number, h: number, s: number, camX: number, L: Light,
): void {
  const layers = [
    { p: 0.12, base: 0.56, a1: 0.028, f1: 0.0017, a2: 0.012, f2: 0.005, ph: 1.2 },
    { p: 0.3, base: 0.6, a1: 0.022, f1: 0.0026, a2: 0.01, f2: 0.008, ph: 4.1 },
  ];
  layers.forEach((ly, i) => {
    let col = mix(HILL_NIGHT[i], HILL_DAY[i], L.day);
    col = mix(col, WARM, L.warm * 0.06);
    // haze toward the sky colour so distant hills read as far away
    col = mix(col, L.bot, 0.22 - i * 0.1);
    ctx.fillStyle = css(col);
    ctx.beginPath();
    ctx.moveTo(0, h);
    for (let sx = 0; sx <= w + 8; sx += 8) {
      const wx = camX * ly.p + sx / s;
      const y = h * (ly.base - ly.a1 * Math.sin(wx * ly.f1 + ly.ph) - ly.a2 * Math.sin(wx * ly.f2 + ly.ph * 2));
      ctx.lineTo(sx, y);
    }
    ctx.lineTo(w, h);
    ctx.closePath();
    ctx.fill();
  });
}

// ---- Austin, TX skyline ------------------------------------------------------

type Style = 'box' | 'slant' | 'step' | 'slender' | 'round' | 'dome';
interface Tower { x: number; w: number; h: number; style: Style }

// Left to right, in skyline units (about 430 wide). The tall ones are after Frost Bank Tower (angled crown),
// The Austonian (stepped with a spire), The Independent (slender) and 360 (rounded top); the low dome is the State Capitol.
const AUSTIN: Tower[] = [
  { x: 0, w: 36, h: 70, style: 'box' },
  { x: 34, w: 30, h: 112, style: 'box' },
  { x: 62, w: 46, h: 178, style: 'slant' },
  { x: 106, w: 30, h: 96, style: 'box' },
  { x: 134, w: 44, h: 246, style: 'step' },
  { x: 176, w: 30, h: 146, style: 'round' },
  { x: 204, w: 34, h: 262, style: 'slender' },
  { x: 236, w: 48, h: 128, style: 'box' },
  { x: 282, w: 28, h: 84, style: 'box' },
  { x: 308, w: 36, h: 118, style: 'box' },
  { x: 342, w: 30, h: 66, style: 'box' },
  { x: 384, w: 56, h: 76, style: 'dome' },
];

// Phoenix, AZ: boxy towers (Chase Tower is the tall flat-topped one), an angled crown, and the Hyatt's rounded top.
const PHOENIX: Tower[] = [
  { x: 0, w: 34, h: 64, style: 'box' },
  { x: 32, w: 30, h: 104, style: 'box' },
  { x: 60, w: 42, h: 158, style: 'slant' },
  { x: 100, w: 30, h: 88, style: 'box' },
  { x: 128, w: 38, h: 226, style: 'box' },
  { x: 164, w: 32, h: 136, style: 'box' },
  { x: 194, w: 34, h: 182, style: 'slant' },
  { x: 226, w: 30, h: 112, style: 'round' },
  { x: 254, w: 40, h: 150, style: 'box' },
  { x: 292, w: 28, h: 92, style: 'box' },
  { x: 318, w: 36, h: 122, style: 'box' },
  { x: 352, w: 30, h: 70, style: 'box' },
  { x: 380, w: 34, h: 96, style: 'box' },
];

function drawSilhouette(
  ctx: CanvasRenderingContext2D, towers: Tower[], left: number, base: number, k: number, col: RGB,
): void {
  ctx.fillStyle = css(col);
  ctx.beginPath();
  for (const t of towers) {
    const x = left + t.x * k, tw = t.w * k, th = t.h * k, yb = base + 60 * k, yt = base - th;
    switch (t.style) {
      case 'box':
        ctx.rect(x, yt, tw, yb - yt);
        break;
      case 'slant': // angled crown
        ctx.moveTo(x, yb); ctx.lineTo(x, yt + 26 * k); ctx.lineTo(x + tw * 0.55, yt); ctx.lineTo(x + tw, yt + 12 * k); ctx.lineTo(x + tw, yb);
        break;
      case 'step': // setbacks and a spire
        ctx.moveTo(x, yb); ctx.lineTo(x, yt + 26 * k); ctx.lineTo(x + tw * 0.12, yt + 26 * k); ctx.lineTo(x + tw * 0.12, yt + 12 * k);
        ctx.lineTo(x + tw * 0.28, yt + 12 * k); ctx.lineTo(x + tw * 0.28, yt + 4 * k); ctx.lineTo(x + tw * 0.48, yt + 4 * k);
        ctx.lineTo(x + tw * 0.48, yt - 10 * k); ctx.lineTo(x + tw * 0.52, yt - 10 * k); ctx.lineTo(x + tw * 0.52, yt + 4 * k);
        ctx.lineTo(x + tw * 0.72, yt + 4 * k); ctx.lineTo(x + tw * 0.72, yt + 12 * k); ctx.lineTo(x + tw * 0.88, yt + 12 * k);
        ctx.lineTo(x + tw * 0.88, yt + 26 * k); ctx.lineTo(x + tw, yt + 26 * k); ctx.lineTo(x + tw, yb);
        break;
      case 'slender': // narrow tower with a tapered top
        ctx.moveTo(x, yb); ctx.lineTo(x, yt + 22 * k); ctx.lineTo(x + tw * 0.18, yt); ctx.lineTo(x + tw * 0.82, yt);
        ctx.lineTo(x + tw, yt + 22 * k); ctx.lineTo(x + tw, yb);
        break;
      case 'round': // rounded crown
        ctx.moveTo(x, yb); ctx.lineTo(x, yt + tw / 2); ctx.arc(x + tw / 2, yt + tw / 2, tw / 2, Math.PI, 0); ctx.lineTo(x + tw, yb);
        break;
      case 'dome': { // state capitol: wide base, drum, dome, lantern and finial
        const cx = x + tw / 2;
        ctx.rect(x, yt + th * 0.45, tw, yb - (yt + th * 0.45));
        ctx.rect(cx - tw * 0.2, yt + th * 0.3, tw * 0.4, th * 0.2);
        ctx.moveTo(cx - tw * 0.22, yt + th * 0.3);
        ctx.ellipse(cx, yt + th * 0.3, tw * 0.22, th * 0.26, 0, Math.PI, 0);
        ctx.rect(cx - tw * 0.04, yt - th * 0.02, tw * 0.08, th * 0.1);
        ctx.rect(cx - tw * 0.012, yt - th * 0.14, tw * 0.024, th * 0.14);
        break;
      }
    }
  }
  ctx.fill();
}

/** Two flat, hazed skyline silhouettes behind the hills: Phoenix far back, Austin in front. They barely drift as you walk. */
export function drawSkyline(
  ctx: CanvasRenderingContext2D, w: number, h: number, L: Light, scroll: number,
): void {
  const k = Math.min((h / 720) * 0.95, (w * 0.9) / 440); // skyline unit in pixels, never wider than the screen
  const width = 440 * k;
  const left = Math.min(w - width - 12, Math.max(12, w * 0.6)) - scroll * 0.04;
  const base = h * (HORIZON - 0.02);
  let col = mix(hex('#0b0f1e'), hex('#5d76a6'), L.day);
  col = mix(col, WARM, L.warm * 0.12);
  // Phoenix: further away, so smaller, slower and more hazed; offset left so both read
  const far = mix(col, L.bot, 0.68);
  const offset = width * (w < 700 ? 0.22 : 0.62); // narrower screens overlap the two more so both show
  ctx.globalAlpha = 0.2;
  drawSilhouette(ctx, PHOENIX, left - offset + scroll * 0.02, base - 4 * k, k * 0.86, far);
  // Austin: toned down with more haze
  ctx.globalAlpha = 0.34;
  drawSilhouette(ctx, AUSTIN, left, base, k, mix(col, L.bot, 0.5));
  ctx.globalAlpha = 1;
}
